import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import SubscriptionRequiredModal from '../../../components/ui/SubscriptionRequiredModal';
import { isSubscriptionError } from '../../../lib/subscriptionGate';

type CatalogStructure = 'semestru' | 'trimestru' | 'module';

type GradebookConfigLite = {
  id: number;
  school_year: string;
  structure: CatalogStructure | string;
};

type CatalogSubjectLite = {
  id: number;
  name: string;
  professor_id?: number | null;
  professor_name?: string | null;
  gradebook_config_id?: number | null;
  period_index?: number | null;
};

type CourseFile = {
  id: number;
  file_url: string;
  original_name: string;
};

type CourseItem = {
  id: number;
  subject_id: number | null;
  subject: string | null;
  title: string;
  comment: string | null;
  type?: string | null;
  created_at: string;
  files: CourseFile[];
};

interface Props {
  classroomId: number;
}

function cfgLabel(cfg: GradebookConfigLite) {
  const s = (cfg.structure || '').toLowerCase();
  const struct = s === 'semestru' ? 'Semestru' : s === 'trimestru' ? 'Trimestru' : 'Module';
  return `${cfg.school_year} • ${struct}`;
}

function periodLabels(structure: CatalogStructure | string): string[] {
  const s = (structure || '').toString().toLowerCase();
  if (s === 'semestru') return ['Semestrul 1', 'Semestrul 2'];
  if (s === 'trimestru') return ['Trimestrul 1', 'Trimestrul 2', 'Trimestrul 3'];
  return ['Modul 1', 'Modul 2', 'Modul 3', 'Modul 4', 'Modul 5'];
}

const CursuriPanel: React.FC<Props> = ({ classroomId }) => {
  const { me } = useAuth();
  const apiBase = useMemo(() => (api.defaults.baseURL || 'http://localhost:8000').toString().replace(/\/$/, ''), []);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [configs, setConfigs] = useState<GradebookConfigLite[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const selectedConfig = useMemo(
    () => configs.find((c) => Number(c.id) === Number(selectedConfigId)) || null,
    [configs, selectedConfigId]
  );
  const [periodIndex, setPeriodIndex] = useState(0);

  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjects, setSubjects] = useState<CatalogSubjectLite[]>([]);
  const mySubjects = useMemo(() => {
    if (!me?.id) return [];
    const mine = subjects.filter((s) => Number(s.professor_id) === Number(me.id));
    return mine.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [me?.id, subjects]);

  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);

  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [cursuri, setCursuri] = useState<CourseItem[]>([]);

  const doDownload = async (fileId: number) => {
    try {
      const resp = await api.get(`/api/cursuri/fisiere/${encodeURIComponent(String(fileId))}/download`);
      const urlPath = String((resp.data as any)?.url || '');
      const base = String((api.defaults.baseURL || window.location.origin) as any).replace(/\/$/, '');
      const fullUrl = urlPath ? `${base}${urlPath.startsWith('/') ? urlPath : `/${urlPath}`}` : '';
      if (fullUrl) {
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        setUpgradeOpen(true);
        return;
      }
      alert(e?.response?.data?.detail || 'Nu am putut descărca fișierul.');
    }
  };

  const [editingTitleId, setEditingTitleId] = useState<number | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newComment, setNewComment] = useState('');

  const loadConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/catalog/config/all/${classroomId}`);
      const list = Array.isArray(resp.data) ? (resp.data as GradebookConfigLite[]) : [];
      setConfigs(list);
      if (!selectedConfigId && list.length) setSelectedConfigId(list[0].id);
      if (selectedConfigId && list.every((c) => Number(c.id) !== Number(selectedConfigId))) setSelectedConfigId(list[0]?.id ?? null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu s-a putut incarca lista de cataloage.');
      setConfigs([]);
      setSelectedConfigId(null);
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async (cfgId: number, pIdx: number) => {
    setSubjectsLoading(true);
    try {
      const resp = await api.get(`/api/catalog/classroom/${classroomId}/overview`, { params: { config_id: cfgId, period_index: pIdx } });
      const list = Array.isArray(resp.data?.subjects) ? (resp.data.subjects as CatalogSubjectLite[]) : [];
      setSubjects(list);
    } catch {
      setSubjects([]);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const loadCursuri = async (cfgId: number, pIdx: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/cursuri/lista/${classroomId}`, { params: { gradebook_config_id: cfgId, period_index: pIdx } });
      setCursuri(Array.isArray(res.data) ? (res.data as CourseItem[]) : []);
    } catch (err: any) {
      setCursuri([]);
      setError(err?.response?.data?.detail || 'Eroare la incarcarea cursurilor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroomId]);

  useEffect(() => {
    if (!selectedConfigId) return;
    setPeriodIndex(0);
  }, [selectedConfigId]);

  useEffect(() => {
    if (!selectedConfigId) {
      setSubjects([]);
      setSelectedSubjectId(null);
      setCursuri([]);
      return;
    }
    void loadSubjects(selectedConfigId, periodIndex);
    void loadCursuri(selectedConfigId, periodIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroomId, selectedConfigId, periodIndex]);

  useEffect(() => {
    if (!mySubjects.length) {
      setSelectedSubjectId(null);
      return;
    }
    if (selectedSubjectId && mySubjects.some((s) => Number(s.id) === Number(selectedSubjectId))) return;
    setSelectedSubjectId(mySubjects[0].id);
  }, [mySubjects, selectedSubjectId]);

  const grouped = useMemo(() => {
    const map = new Map<string, { subjectId: number | null; subjectName: string; items: CourseItem[] }>();
    for (const t of cursuri) {
      const id = t.subject_id ?? null;
      const name = (t.subject || '').toString().trim() || (id == null ? 'Fara materie' : `Materia #${id}`);
      const key = id == null ? `null:${name}` : String(id);
      const cur = map.get(key);
      if (cur) cur.items.push(t);
      else map.set(key, { subjectId: id, subjectName: name, items: [t] });
    }
    return Array.from(map.values()).sort((a, b) => a.subjectName.localeCompare(b.subjectName));
  }, [cursuri]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedConfigId) {
      setError('Selecteaza un catalog.');
      return;
    }
    if (selectedSubjectId == null) {
      setError('Selecteaza materia.');
      return;
    }
    if (!title.trim()) {
      setError('Completeaza titlul cursului.');
      return;
    }

    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const formData = new FormData();
      formData.append('classroom_id', String(classroomId));
      formData.append('subject_id', String(selectedSubjectId));
      formData.append('gradebook_config_id', String(selectedConfigId));
      formData.append('period_index', String(periodIndex));
      formData.append('type', 'curs');
      formData.append('title', title.trim());
      formData.append('comment', comment);
      files.forEach((f) => formData.append('files', f));

      await api.post('/api/cursuri/adauga', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setTitle('');
      setComment('');
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setInfo('Cursul a fost adaugat.');
      await loadCursuri(selectedConfigId, periodIndex);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Eroare la adaugarea cursului.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Stergi cursul?')) return;
    if (!selectedConfigId) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      await api.delete(`/api/cursuri/sterge/${id}`);
      setInfo('Cursul a fost sters.');
      await loadCursuri(selectedConfigId, periodIndex);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Eroare la stergerea cursului.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTitle = async (id: number) => {
    if (!selectedConfigId) return;
    const nextTitle = newTitle.trim();
    if (!nextTitle) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const form = new FormData();
      form.append('title', nextTitle);
      await api.patch(`/api/cursuri/actualizeaza/${id}`, form);
      setEditingTitleId(null);
      setNewTitle('');
      setInfo('Titlul a fost actualizat.');
      await loadCursuri(selectedConfigId, periodIndex);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Eroare la actualizarea titlului.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateComment = async (id: number) => {
    if (!selectedConfigId) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const form = new FormData();
      form.append('comment', newComment);
      await api.patch(`/api/cursuri/actualizeaza/${id}`, form);
      setEditingCommentId(null);
      setNewComment('');
      setInfo('Comentariul a fost actualizat.');
      await loadCursuri(selectedConfigId, periodIndex);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Eroare la actualizarea comentariului.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadExtraFiles = async (courseId: number, extraFiles: FileList | null) => {
    if (!selectedConfigId) return;
    if (!extraFiles || extraFiles.length === 0) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const formData = new FormData();
      Array.from(extraFiles).forEach((f) => formData.append('files', f));
      await api.post(`/api/cursuri/adauga-fisiere/${courseId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setInfo('Fisierele au fost adaugate.');
      await loadCursuri(selectedConfigId, periodIndex);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Eroare la incarcarea fisierelor.');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    if (!selectedConfigId) {
      await loadConfigs();
      return;
    }
    await Promise.all([loadSubjects(selectedConfigId, periodIndex), loadCursuri(selectedConfigId, periodIndex)]);
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Cursuri</h2>
            <p className="text-sm text-muted-foreground">Incarci cursuri pe materiile tale din catalogul selectat.</p>
          </div>
          <Button variant="outline" iconName="RefreshCcw" iconPosition="left" onClick={refresh} disabled={loading}>
            Reincarca
          </Button>
        </div>

        {error && <div className="mt-3 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>}
        {info && <div className="mt-3 bg-success/10 border border-success/20 text-success rounded-md p-3 text-sm">{info}</div>}

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
          <label className="block text-sm font-medium text-foreground">
            Catalog
            <select
              value={selectedConfigId ?? ''}
              onChange={(e) => setSelectedConfigId(e.target.value ? Number(e.target.value) : null)}
              className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={loading}
            >
              <option value="">-- selecteaza --</option>
              {configs.map((c) => (
                <option key={c.id} value={c.id}>
                  {cfgLabel(c)}
                </option>
              ))}
            </select>
          </label>

          <div className="lg:col-span-2">
            {selectedConfig ? (
              <div className="mt-6 lg:mt-0 flex flex-wrap gap-2">
                {periodLabels(selectedConfig.structure).map((label, idx) => (
                  <button
                    key={label}
                    onClick={() => setPeriodIndex(idx)}
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors duration-150 ${
                      periodIndex === idx ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'
                    }`}
                    disabled={loading}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground mt-6 lg:mt-0">Alege un catalog ca sa vezi perioadele.</div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Adauga curs</h3>

          {!selectedConfigId ? (
            <div className="text-sm text-muted-foreground">Selecteaza un catalog.</div>
          ) : subjectsLoading ? (
            <div className="text-sm text-muted-foreground">Se incarca materiile…</div>
          ) : mySubjects.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Nu ai materii in aceasta perioada. Adauga mai intai o materie in Catalog (Materiile mele).
            </div>
          ) : (
            <form className="grid grid-cols-1 lg:grid-cols-2 gap-3" onSubmit={handleUpload}>
              <label className="block text-sm font-medium text-foreground">
                Materie *
                <select
                  value={selectedSubjectId ?? ''}
                  onChange={(e) => setSelectedSubjectId(e.target.value ? Number(e.target.value) : null)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={loading}
                >
                  {mySubjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {(s.name || '').toString().trim() || `Materia #${s.id}`}
                      {s.professor_name ? ` (${s.professor_name})` : ''}
                    </option>
                  ))}
                </select>
              </label>

              <Input label="Titlu *" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Curs 1" />
              <Input
                label="Comentariu (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Instructiuni"
              />
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-1">Fisiere (optional)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
                  className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-muted file:text-foreground hover:file:bg-muted/80"
                />
              </div>
              <div className="lg:col-span-2 flex items-center justify-end">
                <Button variant="default" iconName="Upload" iconPosition="left" type="submit" disabled={loading}>
                  Incarca
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">Cursuri incarcate</h3>
          <div className="text-xs text-muted-foreground">{cursuri.length} cursuri</div>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground mt-3">Se incarca…</div>
        ) : cursuri.length === 0 ? (
          <div className="text-sm text-muted-foreground mt-3">Nu exista cursuri create in aceasta perioada.</div>
        ) : (
          <div className="mt-3 space-y-6">
            {grouped.map((group) => (
              <div key={`${group.subjectId ?? 'null'}:${group.subjectName}`} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-foreground">{group.subjectName}</div>
                  <div className="text-xs text-muted-foreground">{group.items.length}</div>
                </div>

                {group.items.map((c) => (
                  <div key={c.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        {editingTitleId === c.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              value={newTitle}
                              onChange={(e) => setNewTitle(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                            <Button size="sm" variant="default" onClick={() => handleUpdateTitle(c.id)}>
                              Salveaza
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-foreground truncate">{c.title}</h4>
                            <button
                              className="p-1 rounded hover:bg-muted"
                              onClick={() => {
                                setEditingTitleId(c.id);
                                setNewTitle(c.title);
                              }}
                              aria-label="Editeaza titlu"
                            >
                              <Icon name="Pencil" size={16} className="text-muted-foreground" />
                            </button>
                          </div>
                        )}

                        {editingCommentId === c.id ? (
                          <div className="mt-2 space-y-2">
                            <textarea
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                              rows={3}
                            />
                            <div className="flex justify-end">
                              <Button size="sm" variant="default" onClick={() => handleUpdateComment(c.id)}>
                                Salveaza
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-1 flex items-start gap-2">
                            <p className="text-sm text-muted-foreground whitespace-pre-line flex-1">{c.comment || '—'}</p>
                            <button
                              className="p-1 rounded hover:bg-muted"
                              onClick={() => {
                                setEditingCommentId(c.id);
                                setNewComment(c.comment || '');
                              }}
                              aria-label="Editeaza comentariu"
                            >
                              <Icon name="FilePenLine" size={16} className="text-muted-foreground" />
                            </button>
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                          <Icon name="Calendar" size={14} className="text-muted-foreground" />
                          <span>{new Date(c.created_at).toLocaleString()}</span>
                        </p>
                      </div>

                      <div className="flex items-center gap-1">
                        <label className="p-2 rounded-md hover:bg-muted transition-colors duration-150 cursor-pointer" aria-label="Adauga fisiere">
                          <Icon name="Paperclip" size={18} className="text-muted-foreground" />
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              handleUploadExtraFiles(c.id, e.target.files);
                              e.currentTarget.value = '';
                            }}
                          />
                        </label>
                        <button
                          className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                          onClick={() => handleDelete(c.id)}
                          aria-label="Sterge curs"
                        >
                          <Icon name="Trash2" size={18} className="text-error" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-4">
                      <p className="text-sm font-medium text-foreground mb-2">Fisiere</p>
                      {c.files?.length ? (
                        <ul className="space-y-1">
                          {c.files.map((f, idx) => (
                            <li key={`${c.id}-${idx}`} className="text-sm">
                              <button className="text-primary hover:underline" onClick={() => void doDownload(Number(f.id))}>
                                {f.original_name}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-muted-foreground">—</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <SubscriptionRequiredModal open={upgradeOpen} feature="materials_download" onClose={() => setUpgradeOpen(false)} />
    </div>
  );
};

export default CursuriPanel;
